import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import FilterListIcon from '@mui/icons-material/FilterList';
import InsightsRoundedIcon from '@mui/icons-material/InsightsRounded';
import { useQuery } from '@apollo/client';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Collapse,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { CohortLtvCurveChart } from 'src/components/Retention/CohortLtvCurveChart';
import { CustomerEconomicsTable } from 'src/components/Retention/CustomerEconomicsTable';
import { DaysToSecondPurchaseChart } from 'src/components/Retention/DaysToSecondPurchaseChart';
import {
  FirstPurchaseLtvChart,
  FirstPurchaseLtvList,
} from 'src/components/Retention/FirstPurchaseLtvChart';
import {
  FirstPurchaseFollowUpOrdersChart,
  FirstPurchaseFollowUpOrdersList,
} from 'src/components/Retention/FirstPurchaseFollowUpOrdersChart';
import {
  FirstPurchaseRepeatChart,
  FirstPurchaseRepeatProductList,
} from 'src/components/Retention/FirstPurchaseRepeatChart';
import {
  type CompareDeltaVisual,
  buildCompareDeltaRepeatRatePp,
  buildCompareDeltaVersusBaseline,
  formatCompactCurrency,
  formatCurrency,
  formatPercent,
  formatRatio,
  formatWholeDays,
} from 'src/components/Retention/formatters';
import { InsightsPanel } from 'src/components/Retention/InsightsPanel';
import { MetricCard } from 'src/components/Retention/MetricCard';
import { PurchaseDecayChart } from 'src/components/Retention/PurchaseDecayChart';
import { RetentionLoadingState } from 'src/components/Retention/RetentionLoadingState';
import { SectionCard } from 'src/components/Retention/SectionCard';
import { SecondPurchasesTable } from 'src/components/Retention/SecondPurchasesTable';
import type { RetentionAnalytics } from 'src/components/Retention/types';
import { useTrialStatus } from 'src/hooks/useTrialStatus';
import { useSnackbar } from 'src/providers/snackbar/hook/useSnackbar';
import { useSidebar } from 'src/providers/sidebar/useSidebar';
import palette from 'src/theme/palette';
import { KIPIFY_FILTER_APPLY_BUTTON_CLASS } from 'src/theme/kipifyUi';
import {
  getLastNDaysDateRange,
  getPreviousPeriodDateRange,
  getPreviousYearDateRange,
  getThisMonthDateRange,
} from 'src/utils/dateUtils';
import {
  GET_RETENTION_ANALYTICS,
  GET_RETENTION_FILTER_OPTIONS,
} from 'src/utils/gql/retentionQueries';
import {
  getLookbackCompareAutoDisabledMessage,
  getLookbackCompareBlockedMessage,
  getLookbackCustomRangeBlockedMessage,
  getLookbackPlanLimitCaption,
  getLookbackPresetBlockedMessage,
  getLookbackThisMonthBlockedMessage,
} from 'src/utils/lookbackMessages';

/**
 * PDF: larghezza massima sul root (evita tabella 720px in colonna 50%).
 * Niente `position:fixed` / `windowHeight` su html2canvas: spesso producono canvas vuoti.
 */
const RETENTION_PDF_CAPTURE_STYLE_ID = 'kipify-retention-pdf-capture-css';
const RETENTION_PDF_CAPTURE_MAX_W = 920;

const PageContainer = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
  minHeight: '100vh',
  width: '100%',
  boxSizing: 'border-box',
  overflow: 'visible',
  [theme.breakpoints.up('sm')]: {
    marginLeft: '240px',
    width: 'calc(100% - 240px)',
    maxWidth: 'calc(100% - 240px)',
  },
  [theme.breakpoints.down('md')]: {
    marginLeft: '220px',
    width: 'calc(100% - 220px)',
    maxWidth: 'calc(100% - 220px)',
  },
  [theme.breakpoints.down('sm')]: {
    marginLeft: 0,
    width: '100%',
    marginTop: '60px',
    maxWidth: '100%',
  },
}));

const ContentContainer = styled(Box)(() => ({
  width: '100%',
  maxWidth: '100%',
  margin: 0,
  padding: '20px 20px 28px',
  boxSizing: 'border-box',
  minHeight: '100%',
}));

/** Nel tema Kipify `palette.grey[700]` è #DCE0E8 (surface), non testo leggibile su bianco. */
const retentionTextPrimary = palette.text.primary;
const retentionTextSecondary = palette.text.secondary;

const labelSx = {
  display: 'block',
  mb: 0.75,
  color: retentionTextPrimary,
  fontWeight: 600,
  fontSize: '0.825rem',
} as const;

const controlSx = {
  '& .MuiOutlinedInput-root': {
    backgroundColor: palette.common.white,
    borderRadius: 2,
    color: retentionTextPrimary,
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(15, 23, 42, 0.14)',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(15, 23, 42, 0.26)',
  },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: palette.primary.main,
    borderWidth: 1,
  },
  '& .MuiSelect-icon': {
    color: retentionTextSecondary,
  },
  '& input::placeholder': {
    color: palette.text.disabled,
    opacity: 1,
  },
} as const;

/** Autocomplete Product: stessa scala tipografica di label/filtri (non il default più grande del menu). */
const productAutocompleteSx = [
  controlSx,
  {
    '& .MuiOutlinedInput-root': {
      fontSize: '0.8125rem',
    },
    '& .MuiOutlinedInput-input': {
      fontSize: '0.8125rem',
      fontWeight: 500,
      lineHeight: 1.35,
      py: '7px',
    },
    '& .MuiAutocomplete-endAdornment .MuiSvgIcon-root': {
      fontSize: 18,
    },
  },
] as const;

const productAutocompletePaperSx = {
  '& .MuiAutocomplete-listbox': {
    maxHeight: 280,
    py: 0.5,
  },
  '& .MuiAutocomplete-option': {
    fontSize: '0.8125rem',
    fontWeight: 500,
    lineHeight: 1.35,
    minHeight: 36,
    py: 0.65,
    px: 1.25,
  },
} as const;

/** Campi dentro il drawer filtri: bordi e testo leggibili su bianco (#fafafa). */
const filterPanelInputSx = {
  '& .MuiOutlinedInput-root': {
    backgroundColor: palette.common.white,
    color: retentionTextPrimary,
    borderRadius: 2,
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(15, 23, 42, 0.14)',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(15, 23, 42, 0.26)',
  },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderWidth: 1,
    borderColor: palette.primary.main,
  },
  '& input::placeholder': {
    color: palette.text.disabled,
    opacity: 1,
  },
} as const;

const filterPanelSelectSx = {
  backgroundColor: 'white',
  color: retentionTextPrimary,
  borderRadius: 2,
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(15, 23, 42, 0.14)',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(15, 23, 42, 0.26)',
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: palette.primary.main,
    borderWidth: 1,
  },
  '& .MuiSvgIcon-root': {
    color: retentionTextSecondary,
  },
};

/** Period + compare: stessa estetica di Product (bordo neutro, focus primary). */
const periodRowControlHeight = 40;
const periodRowControlRadius = 2;
const periodRowControlSx = {
  height: periodRowControlHeight,
  minHeight: periodRowControlHeight,
  borderRadius: periodRowControlRadius,
  backgroundColor: 'white',
  '& .MuiOutlinedInput-root': {
    height: periodRowControlHeight,
    minHeight: periodRowControlHeight,
    borderRadius: periodRowControlRadius,
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(15, 23, 42, 0.14)',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(15, 23, 42, 0.22)',
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: palette.primary.main,
    borderWidth: 1,
  },
  '& .MuiSelect-icon': {
    color: retentionTextSecondary,
  },
} as const;

const periodRowLabelSx = {
  display: 'block',
  mb: 0.5,
  fontSize: '0.75rem',
  fontWeight: 600,
  color: retentionTextSecondary,
  letterSpacing: '0.01em',
} as const;

const periodSelectMenuPaperSx = {
  '& .MuiMenuItem-root': {
    fontSize: '0.8125rem',
    fontWeight: 500,
    minHeight: 36,
    py: 0.75,
  },
} as const;

type InventoryLikeCustomerType = '' | 'First time' | 'recurring';
type RetentionApiSegment = 'all' | 'new' | 'returning';

interface RetentionQueryData {
  getRetentionAnalytics: RetentionAnalytics;
}

interface RetentionFilterOptionsQueryData {
  getRetentionFilterOptions: {
    countries: string[];
    orderTags: string[];
    collectionTitles: string[];
    vendors: string[];
    productTypes: string[];
  };
}

const RetentionPage = () => {
  const theme = useTheme();
  const snackbar = useSnackbar();
  const { collapsed: sidebarCollapsed } = useSidebar();
  const { lookbackDays } = useTrialStatus();
  const containerSx = sidebarCollapsed
    ? {
        [theme.breakpoints.up('sm')]: {
          marginLeft: '72px',
          width: 'calc(100% - 72px)',
          maxWidth: 'calc(100% - 72px)',
        },
      }
    : undefined;

  const [dateRangePreset, setDateRangePreset] = useState<string>('30');
  const [dateRange, setDateRange] = useState(() => getLastNDaysDateRange(30));
  const [showCustomDateRange, setShowCustomDateRange] = useState(false);
  const [tempCustomDates, setTempCustomDates] = useState<{
    from: string;
    to: string;
  } | null>(null);
  const [compareMode, setCompareMode] = useState<string>('none');
  const [compareDateRange, setCompareDateRange] = useState<{
    from: string;
    to: string;
  } | null>(null);
  const [showCustomCompareDates, setShowCustomCompareDates] = useState(false);
  const [tempCustomCompareDates, setTempCustomCompareDates] = useState<{
    from: string;
    to: string;
  } | null>(null);
  const [productId, setProductId] = useState<string>('');
  const [showFilters, setShowFilters] = useState(false);
  const [countryFilter, setCountryFilter] = useState<string[]>([]);
  const [customerTypeFilter, setCustomerTypeFilter] =
    useState<InventoryLikeCustomerType>('');
  const [tagFilter, setTagFilter] = useState<string[]>([]);
  const [collectionFilter, setCollectionFilter] = useState<string[]>([]);
  const [vendorFilter, setVendorFilter] = useState<string[]>([]);
  const [productTypeFilter, setProductTypeFilter] = useState<string[]>([]);
  const [aiReportOpen, setAiReportOpen] = useState(false);
  const [repeatDriversModalOpen, setRepeatDriversModalOpen] = useState(false);
  const [followUpVolumeModalOpen, setFollowUpVolumeModalOpen] = useState(false);
  const [ltvDriversModalOpen, setLtvDriversModalOpen] = useState(false);
  const [secondPairsModalOpen, setSecondPairsModalOpen] = useState(false);
  const [productOptions, setProductOptions] = useState<
    Array<{ id: string; title: string }>
  >([]);

  const lookbackMinDate = useMemo(() => {
    if (lookbackDays === null || lookbackDays === undefined) return null;
    const min = new Date();
    min.setDate(min.getDate() - lookbackDays);
    return min.toISOString().split('T')[0];
  }, [lookbackDays]);

  const canUsePresetDays = useCallback(
    (days: number) => {
      if (lookbackDays === null || lookbackDays === undefined) return true;
      return days <= lookbackDays;
    },
    [lookbackDays],
  );

  const isRangeWithinLookback = useCallback(
    (range: { from: string; to: string } | null | undefined) => {
      if (!range || !lookbackMinDate) return true;
      return range.from >= lookbackMinDate;
    },
    [lookbackMinDate],
  );

  const canUseThisMonthPreset = useMemo(() => {
    if (!lookbackMinDate) return true;
    const thisMonthRange = getThisMonthDateRange();
    return thisMonthRange.from >= lookbackMinDate;
  }, [lookbackMinDate]);

  const previousPeriodRangeCandidate = useMemo(
    () => getPreviousPeriodDateRange(dateRange),
    [dateRange],
  );
  const previousYearRangeCandidate = useMemo(
    () => getPreviousYearDateRange(dateRange),
    [dateRange],
  );
  const canUsePreviousPeriodCompare = useMemo(
    () => isRangeWithinLookback(previousPeriodRangeCandidate),
    [isRangeWithinLookback, previousPeriodRangeCandidate],
  );
  const canUsePreviousYearCompare = useMemo(
    () => isRangeWithinLookback(previousYearRangeCandidate),
    [isRangeWithinLookback, previousYearRangeCandidate],
  );

  useEffect(() => {
    if (compareMode === 'previous_period') {
      setCompareDateRange(getPreviousPeriodDateRange(dateRange));
    } else if (compareMode === 'previous_year') {
      setCompareDateRange(getPreviousYearDateRange(dateRange));
    }
  }, [dateRange, compareMode]);

  useEffect(() => {
    if (compareMode === 'previous_period' && !canUsePreviousPeriodCompare) {
      setCompareMode('none');
      setCompareDateRange(null);
      snackbar(
        getLookbackCompareAutoDisabledMessage('previous_period', lookbackDays),
        'warning',
      );
    } else if (compareMode === 'previous_year' && !canUsePreviousYearCompare) {
      setCompareMode('none');
      setCompareDateRange(null);
      snackbar(
        getLookbackCompareAutoDisabledMessage('previous_year', lookbackDays),
        'warning',
      );
    }
  }, [
    compareMode,
    canUsePreviousPeriodCompare,
    canUsePreviousYearCompare,
    lookbackDays,
    snackbar,
  ]);

  const customerSegmentForApi = useMemo((): RetentionApiSegment => {
    if (customerTypeFilter === 'First time') return 'new';
    if (customerTypeFilter === 'recurring') return 'returning';
    return 'all';
  }, [customerTypeFilter]);

  const { data: retentionFilterOptionsData } =
    useQuery<RetentionFilterOptionsQueryData>(GET_RETENTION_FILTER_OPTIONS, {
      fetchPolicy: 'cache-first',
    });

  const availableOrderTags = useMemo(
    () =>
      retentionFilterOptionsData?.getRetentionFilterOptions?.orderTags ?? [],
    [retentionFilterOptionsData],
  );
  const availableCountries = useMemo(
    () =>
      retentionFilterOptionsData?.getRetentionFilterOptions?.countries ?? [],
    [retentionFilterOptionsData],
  );
  const availableCollectionTitles = useMemo(
    () =>
      retentionFilterOptionsData?.getRetentionFilterOptions?.collectionTitles ??
      [],
    [retentionFilterOptionsData],
  );
  const availableVendors = useMemo(
    () => retentionFilterOptionsData?.getRetentionFilterOptions?.vendors ?? [],
    [retentionFilterOptionsData],
  );
  const availableProductTypes = useMemo(
    () =>
      retentionFilterOptionsData?.getRetentionFilterOptions?.productTypes ?? [],
    [retentionFilterOptionsData],
  );

  const retentionNarrowing = useMemo(() => {
    const countries = countryFilter.map((c) => c.trim()).filter(Boolean);
    const orderTags = tagFilter.map((t) => t.trim()).filter(Boolean);
    const collectionTitles = collectionFilter
      .map((t) => t.trim())
      .filter(Boolean);
    const vendors = vendorFilter.map((t) => t.trim()).filter(Boolean);
    const productTypes = productTypeFilter.map((t) => t.trim()).filter(Boolean);
    if (
      countries.length === 0 &&
      orderTags.length === 0 &&
      collectionTitles.length === 0 &&
      vendors.length === 0 &&
      productTypes.length === 0
    ) {
      return null;
    }
    return {
      ...(countries.length ? { countries } : {}),
      ...(orderTags.length ? { orderTags } : {}),
      ...(collectionTitles.length ? { collectionTitles } : {}),
      ...(vendors.length ? { vendors } : {}),
      ...(productTypes.length ? { productTypes } : {}),
    };
  }, [
    countryFilter,
    tagFilter,
    collectionFilter,
    vendorFilter,
    productTypeFilter,
  ]);

  const retentionQueryVariables = useMemo(
    () => ({
      dateRange: { from: dateRange.from, to: dateRange.to },
      customerSegment: customerSegmentForApi,
      ...(productId ? { productId } : {}),
      narrowing: retentionNarrowing,
    }),
    [
      dateRange.from,
      dateRange.to,
      customerSegmentForApi,
      productId,
      retentionNarrowing,
    ],
  );

  const compareSkip =
    compareMode === 'none' || !compareDateRange?.from || !compareDateRange?.to;

  const compareQueryVariables = useMemo(
    () => ({
      dateRange: {
        from: compareDateRange?.from ?? '',
        to: compareDateRange?.to ?? '',
      },
      customerSegment: customerSegmentForApi,
      ...(productId ? { productId } : {}),
      narrowing: retentionNarrowing,
    }),
    [
      compareDateRange?.from,
      compareDateRange?.to,
      customerSegmentForApi,
      productId,
      retentionNarrowing,
    ],
  );

  const activeFilterCount =
    (productId ? 1 : 0) +
    countryFilter.length +
    (customerTypeFilter ? 1 : 0) +
    tagFilter.length +
    collectionFilter.length +
    vendorFilter.length +
    productTypeFilter.length;

  const { data, loading, error } = useQuery<RetentionQueryData>(
    GET_RETENTION_ANALYTICS,
    {
      variables: retentionQueryVariables,
      fetchPolicy: 'network-only',
    },
  );

  const {
    data: compareQueryData,
    loading: compareLoading,
    error: compareError,
  } = useQuery<RetentionQueryData>(GET_RETENTION_ANALYTICS, {
    skip: compareSkip,
    variables: compareQueryVariables,
    fetchPolicy: 'network-only',
  });

  const analytics = data?.getRetentionAnalytics;
  const compareAnalytics = compareQueryData?.getRetentionAnalytics;
  const hasSecondPurchaseData = useMemo(
    () =>
      (analytics?.daysToSecondPurchaseDistribution ?? []).some(
        (bucket) => bucket.customers > 0,
      ),
    [analytics],
  );
  const hasTableData = (analytics?.mostCommonSecondPurchases?.length ?? 0) > 0;
  const hasInsights = (analytics?.insights?.length ?? 0) > 0;
  const hasLtvProductData =
    (analytics?.firstPurchaseProductsByLtv?.length ?? 0) > 0;
  const hasCustomerEconomicsData =
    (analytics?.customerEconomics?.length ?? 0) > 0;

  const productSelectOptions = useMemo(
    () => [{ id: '', title: 'All products' }, ...productOptions],
    [productOptions],
  );

  const selectedProductOption = useMemo(
    () =>
      productSelectOptions.find((o) => o.id === productId) ??
      productSelectOptions[0],
    [productId, productSelectOptions],
  );

  useEffect(() => {
    if (!analytics) return;
    if (productId) return;

    const nextOptions = analytics.firstPurchaseProductsGeneratingRepeats.map(
      (item) => ({
        id: item.productId,
        title: item.productTitle,
      }),
    );

    setProductOptions(nextOptions);
  }, [analytics, productId]);

  const showComparePeriod =
    compareMode !== 'none' &&
    !!compareDateRange?.from &&
    !!compareDateRange?.to;

  const metricCompareDeltas = useMemo(() => {
    const empty: Record<
      | 'avgSecond'
      | 'avgSame'
      | 'topProduct'
      | 'inactivity'
      | 'ltv'
      | 'grossMarginLtv'
      | 'ltvCacRatio',
      CompareDeltaVisual | null
    > = {
      avgSecond: null,
      avgSame: null,
      topProduct: null,
      inactivity: null,
      ltv: null,
      grossMarginLtv: null,
      ltvCacRatio: null,
    };
    if (
      !showComparePeriod ||
      !compareAnalytics?.summary ||
      !compareDateRange?.from ||
      !compareDateRange?.to
    ) {
      return empty;
    }
    const ps = analytics?.summary;
    const cs = compareAnalytics.summary;
    const from = compareDateRange.from;
    const to = compareDateRange.to;
    const rangeOpts = { compareRangeFrom: from, compareRangeTo: to };

    return {
      avgSecond: buildCompareDeltaVersusBaseline(
        ps?.avgDaysToSecondPurchase,
        cs.avgDaysToSecondPurchase,
        { ...rangeOpts, lowerIsBetter: true },
      ),
      avgSame: buildCompareDeltaVersusBaseline(
        ps?.avgDaysToSameProductReorder,
        cs.avgDaysToSameProductReorder,
        { ...rangeOpts, lowerIsBetter: true },
      ),
      topProduct: buildCompareDeltaRepeatRatePp(
        ps?.topFirstPurchaseProduct?.repeatRate,
        cs.topFirstPurchaseProduct?.repeatRate,
        from,
        to,
      ),
      inactivity: buildCompareDeltaVersusBaseline(
        ps?.inactivityWindowDays,
        cs.inactivityWindowDays,
        { ...rangeOpts, lowerIsBetter: true },
      ),
      ltv: buildCompareDeltaVersusBaseline(
        ps?.cumulativeLtv,
        cs.cumulativeLtv,
        {
          ...rangeOpts,
          lowerIsBetter: false,
        },
      ),
      grossMarginLtv: buildCompareDeltaVersusBaseline(
        ps?.cumulativeGrossMarginLtv,
        cs.cumulativeGrossMarginLtv,
        { ...rangeOpts, lowerIsBetter: false },
      ),
      ltvCacRatio: buildCompareDeltaVersusBaseline(
        ps?.ltvToCacRatio,
        cs.ltvToCacRatio,
        { ...rangeOpts, lowerIsBetter: false },
      ),
    };
  }, [showComparePeriod, compareAnalytics, analytics, compareDateRange]);

  const waitForImages = useCallback((element: HTMLElement): Promise<void> => {
    return new Promise((resolve) => {
      const images = element.querySelectorAll('img');
      if (images.length === 0) {
        resolve();
        return;
      }

      let loadedCount = 0;
      const totalImages = images.length;

      const checkComplete = () => {
        loadedCount++;
        if (loadedCount === totalImages) {
          resolve();
        }
      };

      images.forEach((img) => {
        if (img.complete) {
          checkComplete();
        } else {
          img.onload = checkComplete;
          img.onerror = checkComplete;
        }
      });
    });
  }, []);

  const loadScript = useCallback(
    (src: string, dataAttr: string): Promise<void> => {
      const attr = `data-kipify-${dataAttr}`;
      return new Promise((resolve, reject) => {
        if (document.querySelector(`script[${attr}="1"]`)) {
          resolve();
          return;
        }
        const script = document.createElement('script');
        script.setAttribute(attr, '1');
        script.src = src;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error(`Failed to load ${dataAttr}`));
        document.head.appendChild(script);
      });
    },
    [],
  );

  const ensurePdfEngineLoaded = useCallback(async (): Promise<void> => {
    const win = window as any;
    if (!win.html2canvas) {
      await loadScript(
        'https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js',
        'html2canvas',
      );
    }
    if (!win.jspdf?.jsPDF && !win.jsPDF) {
      await loadScript(
        'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js',
        'jspdf',
      );
    }
  }, [loadScript]);

  /**
   * Export da una copia neutra del report: evita offset della sidebar/layout
   * desktop che nei tentativi precedenti finivano nel PDF come crop laterale.
   */
  const downloadAsPDF = useCallback(async () => {
    const sourceElement = document.querySelector(
      '.kipify-report',
    ) as HTMLElement | null;
    let captureStyleEl: HTMLStyleElement | null = null;
    let captureRoot: HTMLDivElement | null = null;
    let captureElement: HTMLElement | null = null;

    const tearDown = () => {
      captureRoot?.remove();
      captureStyleEl?.remove();
    };

    try {
      if (!sourceElement) {
        snackbar('Impossibile trovare il contenuto da esportare.', 'error');
        return;
      }

      await ensurePdfEngineLoaded();
      const win = window as any;
      const html2canvas = win.html2canvas;
      const JsPDF = win.jspdf?.jsPDF ?? win.jsPDF;
      if (!html2canvas || !JsPDF) {
        snackbar(
          'PDF: librerie non caricate. Ricarica la pagina e riprova.',
          'error',
        );
        return;
      }

      document.getElementById(RETENTION_PDF_CAPTURE_STYLE_ID)?.remove();
      captureStyleEl = document.createElement('style');
      captureStyleEl.id = RETENTION_PDF_CAPTURE_STYLE_ID;
      captureStyleEl.textContent = `
        .retention-pdf-capture { overflow: visible !important; }
        .retention-pdf-capture-root {
          position: fixed !important;
          left: -10000px !important;
          top: 0 !important;
          width: ${RETENTION_PDF_CAPTURE_MAX_W}px !important;
          padding: 0 !important;
          margin: 0 !important;
          pointer-events: none !important;
          background: #ffffff !important;
        }
        .retention-pdf-capture .retention-pdf-hero-accent,
        .retention-pdf-capture .retention-pdf-hero-shell {
          margin-left: 0 !important;
          margin-right: 0 !important;
        }
        .retention-pdf-capture .retention-pdf-hero-shell { overflow: visible !important; }
        .retention-pdf-capture .header-actions { display: none !important; }
        .retention-pdf-capture .MuiTableContainer-root {
          overflow: visible !important;
          overflow-x: visible !important;
          max-width: none !important;
        }
        .retention-pdf-capture .MuiGrid-grid-md-6 .MuiTable-root,
        .retention-pdf-capture .MuiGrid-grid-lg-6 .MuiTable-root {
          min-width: 0 !important;
          width: 100% !important;
          table-layout: auto !important;
        }
        .retention-pdf-capture .MuiGrid-grid-md-6 colgroup,
        .retention-pdf-capture .MuiGrid-grid-lg-6 colgroup { display: none !important; }
        .retention-pdf-capture .retention-pdf-unclamp {
          display: block !important;
          -webkit-line-clamp: unset !important;
          -webkit-box-orient: unset !important;
          overflow: visible !important;
          white-space: normal !important;
          text-overflow: unset !important;
          word-break: break-word !important;
        }
        .retention-pdf-capture .retention-pdf-caption-wrap {
          white-space: normal !important;
          overflow: visible !important;
          text-overflow: unset !important;
        }
        .retention-pdf-capture .retention-pdf-chart-fit {
          width: 100% !important;
          max-width: 100% !important;
          box-sizing: border-box !important;
          padding-left: 12px !important;
          padding-right: 86px !important;
          overflow: visible !important;
        }
        .retention-pdf-capture .retention-pdf-chart-canvas {
          height: 184px !important;
          overflow: visible !important;
        }
        .retention-pdf-capture .retention-pdf-chart-canvas .recharts-responsive-container {
          width: calc(100% / 0.72) !important;
          height: calc(100% / 0.72) !important;
          transform: scale(0.72) !important;
          transform-origin: top left !important;
        }
        .retention-pdf-capture .retention-pdf-chart-canvas .recharts-wrapper,
        .retention-pdf-capture .retention-pdf-chart-canvas .recharts-surface {
          overflow: visible !important;
        }
        .retention-pdf-capture .retention-pdf-chart-canvas .recharts-line,
        .retention-pdf-capture .retention-pdf-chart-canvas .recharts-bar-rectangles,
        .retention-pdf-capture .retention-pdf-chart-canvas .recharts-scatter,
        .retention-pdf-capture .retention-pdf-chart-canvas .recharts-dot {
          clip-path: none !important;
        }
        .retention-pdf-capture .retention-pdf-chart-canvas .recharts-cartesian-axis-tick-value,
        .retention-pdf-capture .retention-pdf-chart-canvas .recharts-label {
          font-size: 10px !important;
        }
        .retention-pdf-capture .MuiGrid-container > .MuiGrid-item {
          max-width: 100% !important;
          flex-basis: 100% !important;
        }
        .retention-pdf-capture .retention-insight-card-header { flex-wrap: wrap !important; }
        .retention-pdf-capture .MuiGrid-item,
        .retention-pdf-capture .MuiCard-root,
        .retention-pdf-capture .MuiAlert-root,
        .retention-pdf-capture .MuiTableContainer-root {
          break-inside: avoid !important;
          page-break-inside: avoid !important;
        }
      `;
      document.head.appendChild(captureStyleEl);

      captureRoot = document.createElement('div');
      captureRoot.className = 'retention-pdf-capture-root';
      captureElement = sourceElement.cloneNode(true) as HTMLElement;
      captureElement.classList.add('retention-pdf-capture');
      captureElement.style.width = `${RETENTION_PDF_CAPTURE_MAX_W}px`;
      captureElement.style.maxWidth = `${RETENTION_PDF_CAPTURE_MAX_W}px`;
      captureElement.style.minWidth = `${RETENTION_PDF_CAPTURE_MAX_W}px`;
      captureElement.style.margin = '0';
      captureElement.style.padding = '20px 20px 28px';
      captureElement.style.boxSizing = 'border-box';
      captureRoot.appendChild(captureElement);
      document.body.appendChild(captureRoot);

      await waitForImages(captureElement);
      await new Promise<void>((resolve) =>
        requestAnimationFrame(() => requestAnimationFrame(() => resolve())),
      );
      await new Promise<void>((resolve) => setTimeout(resolve, 220));

      const captureRect = captureElement.getBoundingClientRect();
      const breakCandidateSet = new Set<HTMLElement>();

      Array.from(captureElement.children).forEach((child) => {
        if (child instanceof HTMLElement) breakCandidateSet.add(child);
      });
      Array.from(
        captureElement.querySelectorAll('[data-retention-pdf-block]'),
      ).forEach((node) => {
        if (node instanceof HTMLElement) breakCandidateSet.add(node);
      });

      const breakBoundariesPx = Array.from(breakCandidateSet)
        .filter((node) => {
          const style = window.getComputedStyle(node);
          return style.display !== 'none' && style.visibility !== 'hidden';
        })
        .map((node) => {
          const rect = node.getBoundingClientRect();
          return Math.round(rect.top - captureRect.top);
        })
        .filter((top) => top > 0)
        .sort((a, b) => a - b);

      const canvas = await html2canvas(captureElement, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        logging: false,
        scrollX: 0,
        scrollY: 0,
        width: RETENTION_PDF_CAPTURE_MAX_W,
        windowWidth: RETENTION_PDF_CAPTURE_MAX_W,
      });
      const canvasScaleY =
        canvas.height / Math.max(1, captureElement.scrollHeight);
      const scaledBreakBoundariesPx = breakBoundariesPx
        .map((boundary) => Math.round(boundary * canvasScaleY))
        .filter((boundary, index, arr) => {
          return boundary > 0 && (index === 0 || boundary !== arr[index - 1]);
        });

      const pdf = new JsPDF({
        unit: 'pt',
        format: 'a4',
        orientation: 'portrait',
      });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const pageHeightPx = Math.floor((pageH * canvas.width) / pageW);

      let renderedHeightPx = 0;
      let pageIndex = 0;

      while (renderedHeightPx < canvas.height) {
        const targetPageEndPx = Math.min(
          renderedHeightPx + pageHeightPx,
          canvas.height,
        );
        const minComfortableFillPx = renderedHeightPx + pageHeightPx * 0.55;
        const preferredBreakPx = scaledBreakBoundariesPx
          .filter(
            (top) => top > minComfortableFillPx && top < targetPageEndPx - 8,
          )
          .at(-1);
        const fallbackBreakPx = scaledBreakBoundariesPx
          .filter(
            (top) =>
              top > renderedHeightPx + pageHeightPx * 0.3 &&
              top < targetPageEndPx - 8,
          )
          .at(-1);
        const pageEndPx =
          preferredBreakPx ?? fallbackBreakPx ?? targetPageEndPx;
        const sliceHeightPx = Math.max(1, pageEndPx - renderedHeightPx);
        const pageCanvas = document.createElement('canvas');
        pageCanvas.width = canvas.width;
        pageCanvas.height = sliceHeightPx;
        const ctx = pageCanvas.getContext('2d');
        if (!ctx) break;

        ctx.drawImage(
          canvas,
          0,
          renderedHeightPx,
          canvas.width,
          sliceHeightPx,
          0,
          0,
          canvas.width,
          sliceHeightPx,
        );

        const pageImg = pageCanvas.toDataURL('image/jpeg', 0.98);
        const renderedPageHeightPt = (sliceHeightPx * pageW) / canvas.width;
        if (pageIndex > 0) pdf.addPage();
        pdf.addImage(
          pageImg,
          'JPEG',
          0,
          0,
          pageW,
          renderedPageHeightPt,
          undefined,
          'FAST',
        );

        renderedHeightPx += sliceHeightPx;
        pageIndex += 1;
      }

      pdf.save(`kipify-retention-report-${dateRange.from}-${dateRange.to}.pdf`);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      snackbar('Errore durante il download del PDF', 'error');
    } finally {
      tearDown();
    }
  }, [
    dateRange.from,
    dateRange.to,
    ensurePdfEngineLoaded,
    snackbar,
    waitForImages,
  ]);

  const clearFilters = () => {
    setCustomerTypeFilter('');
    setProductId('');
    setCountryFilter([]);
    setTagFilter([]);
    setCollectionFilter([]);
    setVendorFilter([]);
    setProductTypeFilter([]);
    setDateRangePreset('30');
    setShowCustomDateRange(false);
    setTempCustomDates(null);
    setCompareMode('none');
    setCompareDateRange(null);
    setShowCustomCompareDates(false);
    setTempCustomCompareDates(null);
    setDateRange(getLastNDaysDateRange(30));
  };

  const listLoading = loading || (!compareSkip && compareLoading);

  return (
    <PageContainer sx={containerSx}>
      <ContentContainer className="kipify-report">
        <Box
          className="retention-pdf-hero-accent"
          sx={{
            height: 4,
            borderRadius: '0 0 4px 4px',
            background: `linear-gradient(90deg, ${palette.primary.main}, ${palette.primary.light})`,
            mb: 2,
            mx: -0.5,
          }}
        />

        <Box
          className="retention-pdf-hero-shell"
          sx={{
            mb: 2,
            pb: 2,
            borderBottom: '1px solid',
            borderColor: palette.grey[200],
            background: `linear-gradient(180deg, ${palette.primary.main}05 0%, ${palette.primary.main}02 25%, transparent 70%)`,
            mt: -0.5,
            pt: 2,
            px: 2.5,
            mx: -2.5,
            borderRadius: 2,
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 1,
              background: `linear-gradient(90deg, transparent, ${palette.primary.main}18, transparent)`,
              opacity: 0.6,
            },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 2,
              flexWrap: 'wrap',
            }}
          >
            <Box>
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.75,
                  px: 1.25,
                  py: 0.5,
                  borderRadius: '999px',
                  bgcolor: `${palette.primary.main}18`,
                  border: '1px solid',
                  borderColor: `${palette.primary.main}35`,
                  mb: 1,
                }}
              >
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    bgcolor: palette.primary.main,
                    flexShrink: 0,
                    boxShadow: `0 0 0 2px ${palette.primary.main}40`,
                  }}
                />
                <Typography
                  sx={{
                    color: palette.primary.main,
                    fontWeight: 700,
                    letterSpacing: 1.1,
                    fontSize: '0.7rem',
                  }}
                >
                  RETENTION
                </Typography>
              </Box>
              <Box sx={{ position: 'relative', display: 'inline-block' }}>
                <Typography
                  variant="h4"
                  sx={{
                    color: palette.grey[800],
                    fontWeight: 800,
                    letterSpacing: '-0.025em',
                    lineHeight: 1.2,
                  }}
                >
                  Retention Intelligence
                </Typography>
                <Box
                  sx={{
                    width: '60%',
                    height: 3,
                    borderRadius: '0 0 2px 2px',
                    background: `linear-gradient(90deg, ${palette.primary.main}, transparent)`,
                    opacity: 0.5,
                    mt: 0.5,
                  }}
                />
              </Box>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 1, mb: 1.5, maxWidth: 720 }}
              >
                Understand when customers buy again, what they buy next, and
                when they are likely to stop purchasing.
              </Typography>
            </Box>

            <Stack
              direction="row"
              spacing={1}
              flexWrap="wrap"
              useFlexGap
              className="header-actions"
            >
              <Button
                variant="outlined"
                startIcon={<FileDownloadOutlinedIcon />}
                disabled={listLoading || !analytics}
                onClick={() => {
                  void downloadAsPDF();
                }}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  color: retentionTextPrimary,
                  borderColor: 'rgba(15,23,42,0.2)',
                }}
              >
                Export
              </Button>
              <Button
                variant="contained"
                startIcon={<AutoAwesomeRoundedIcon />}
                disabled={!hasInsights}
                onClick={() => setAiReportOpen(true)}
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  bgcolor: '#6366f1',
                  boxShadow: '0 12px 28px -18px rgba(99,102,241,0.7)',
                  '&:hover': { bgcolor: '#5458ee' },
                }}
              >
                Open playbook
              </Button>
            </Stack>
          </Box>
        </Box>

        <Box
          sx={{
            p: 2,
            borderRadius: 2.5,
            border: '1px solid',
            borderColor: 'rgba(15, 23, 42, 0.08)',
            bgcolor: palette.common.white,
            boxShadow: '0 4px 24px -12px rgba(15, 23, 42, 0.1)',
            mb: 2,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-end',
              flexWrap: { xs: 'wrap', lg: 'nowrap' },
              gap: 1.5,
              rowGap: 1.25,
              p: 1.5,
              borderRadius: 2,
              bgcolor: '#f8fafc',
              border: '1px solid rgba(15, 23, 42, 0.06)',
              mb: 0,
              overflowX: { lg: 'auto' },
            }}
          >
            <Box sx={{ flexShrink: 0 }}>
              <Typography component="label" sx={periodRowLabelSx}>
                Period
              </Typography>
              <FormControl
                size="small"
                sx={{ width: 145, ...periodRowControlSx }}
              >
                <Select
                  value={dateRangePreset}
                  MenuProps={{
                    PaperProps: { sx: periodSelectMenuPaperSx },
                  }}
                  onChange={(e) => {
                    const preset = e.target.value;
                    if (preset !== 'custom' && preset !== 'this_month') {
                      const days = parseInt(preset, 10);
                      if (!isNaN(days) && !canUsePresetDays(days)) {
                        snackbar(
                          getLookbackPresetBlockedMessage(lookbackDays),
                          'warning',
                        );
                        return;
                      }
                    }
                    if (preset === 'this_month' && !canUseThisMonthPreset) {
                      snackbar(
                        getLookbackThisMonthBlockedMessage(lookbackDays),
                        'warning',
                      );
                      return;
                    }
                    setDateRangePreset(preset);
                    setShowCustomDateRange(preset === 'custom');
                    setTempCustomDates(null);
                    if (preset === 'this_month') {
                      const newDateRange = getThisMonthDateRange();
                      setDateRange(newDateRange);
                      if (compareMode === 'previous_period')
                        setCompareDateRange(
                          getPreviousPeriodDateRange(newDateRange),
                        );
                      else if (compareMode === 'previous_year')
                        setCompareDateRange(
                          getPreviousYearDateRange(newDateRange),
                        );
                    } else if (preset !== 'custom') {
                      const days = parseInt(preset, 10);
                      const newDateRange = getLastNDaysDateRange(days);
                      setDateRange(newDateRange);
                      if (compareMode === 'previous_period')
                        setCompareDateRange(
                          getPreviousPeriodDateRange(newDateRange),
                        );
                      else if (compareMode === 'previous_year')
                        setCompareDateRange(
                          getPreviousYearDateRange(newDateRange),
                        );
                    } else {
                      setTempCustomDates({
                        from: dateRange.from,
                        to: dateRange.to,
                      });
                    }
                  }}
                  sx={{
                    '& .MuiSelect-select': {
                      py: 0,
                      height: periodRowControlHeight - 2,
                      boxSizing: 'border-box',
                      display: 'flex',
                      alignItems: 'center',
                      fontSize: '0.8125rem',
                      fontWeight: 500,
                      color: retentionTextPrimary,
                    },
                  }}
                >
                  <MenuItem value="7" disabled={!canUsePresetDays(7)}>
                    Last 7 days
                  </MenuItem>
                  <MenuItem value="30" disabled={!canUsePresetDays(30)}>
                    Last 30 days
                  </MenuItem>
                  <MenuItem value="90" disabled={!canUsePresetDays(90)}>
                    Last 90 days
                  </MenuItem>
                  <MenuItem
                    value="this_month"
                    disabled={!canUseThisMonthPreset}
                  >
                    This month
                  </MenuItem>
                  <MenuItem value="custom">Custom range</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {showCustomDateRange && (
              <>
                <TextField
                  type="date"
                  label="From"
                  size="small"
                  value={tempCustomDates?.from ?? dateRange.from}
                  onChange={(e) => {
                    const v = e.target.value;
                    const t = tempCustomDates?.to ?? dateRange.to;
                    setTempCustomDates(
                      t && v > t
                        ? { from: v, to: v }
                        : { from: v, to: tempCustomDates?.to ?? dateRange.to },
                    );
                  }}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    min: lookbackMinDate || undefined,
                    max: (tempCustomDates?.to ?? dateRange.to) || undefined,
                  }}
                  sx={{ flexShrink: 0, width: 140, ...periodRowControlSx }}
                />
                <TextField
                  type="date"
                  label="To"
                  size="small"
                  sx={{ flexShrink: 0, width: 140, ...periodRowControlSx }}
                  value={tempCustomDates?.to ?? dateRange.to}
                  onChange={(e) => {
                    const v = e.target.value;
                    const f = tempCustomDates?.from ?? dateRange.from;
                    setTempCustomDates(
                      f && v < f
                        ? { from: v, to: v }
                        : {
                            from: tempCustomDates?.from ?? dateRange.from,
                            to: v,
                          },
                    );
                  }}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    min: (tempCustomDates?.from ?? dateRange.from) || undefined,
                    max: new Date().toISOString().split('T')[0],
                  }}
                />
                <Button
                  variant="contained"
                  size="small"
                  className={KIPIFY_FILTER_APPLY_BUTTON_CLASS}
                  onClick={() => {
                    if (
                      tempCustomDates?.from &&
                      tempCustomDates?.to &&
                      tempCustomDates.from <= tempCustomDates.to
                    ) {
                      if (!isRangeWithinLookback(tempCustomDates)) {
                        snackbar(
                          getLookbackCustomRangeBlockedMessage(
                            lookbackDays,
                            'date',
                          ),
                          'warning',
                        );
                        return;
                      }
                      setDateRange(tempCustomDates);
                      setTempCustomDates(null);
                    }
                  }}
                  disabled={
                    !tempCustomDates?.from ||
                    !tempCustomDates?.to ||
                    tempCustomDates.from > tempCustomDates.to
                  }
                  sx={{
                    flexShrink: 0,
                    height: periodRowControlHeight,
                    minHeight: periodRowControlHeight,
                    borderRadius: periodRowControlRadius,
                    bgcolor: palette.primary.main,
                    color: palette.primary.contrastText,
                    '&:hover': {
                      bgcolor: palette.primary.light,
                      opacity: 0.9,
                    },
                    minWidth: 60,
                  }}
                >
                  Apply
                </Button>
              </>
            )}

            <Box sx={{ flexShrink: 0 }}>
              <Typography component="label" sx={periodRowLabelSx}>
                Compare
              </Typography>
              <FormControl
                size="small"
                sx={{ width: 155, ...periodRowControlSx }}
              >
                <Select
                  value={compareMode}
                  MenuProps={{
                    PaperProps: { sx: periodSelectMenuPaperSx },
                  }}
                  onChange={(e) => {
                    const mode = e.target.value;
                    if (
                      mode === 'previous_period' &&
                      !canUsePreviousPeriodCompare
                    ) {
                      snackbar(
                        getLookbackCompareBlockedMessage(
                          'previous_period',
                          lookbackDays,
                        ),
                        'warning',
                      );
                      return;
                    }
                    if (
                      mode === 'previous_year' &&
                      !canUsePreviousYearCompare
                    ) {
                      snackbar(
                        getLookbackCompareBlockedMessage(
                          'previous_year',
                          lookbackDays,
                        ),
                        'warning',
                      );
                      return;
                    }
                    setCompareMode(mode);
                    if (mode === 'none') {
                      setCompareDateRange(null);
                      setShowCustomCompareDates(false);
                      setTempCustomCompareDates(null);
                    } else if (mode === 'previous_period') {
                      setCompareDateRange(
                        getPreviousPeriodDateRange(dateRange),
                      );
                      setShowCustomCompareDates(false);
                    } else if (mode === 'previous_year') {
                      setCompareDateRange(getPreviousYearDateRange(dateRange));
                      setShowCustomCompareDates(false);
                    } else if (mode === 'custom') {
                      setShowCustomCompareDates(true);
                      setTempCustomCompareDates(
                        compareDateRange || {
                          from: dateRange.from,
                          to: dateRange.to,
                        },
                      );
                    }
                  }}
                  sx={{
                    '& .MuiSelect-select': {
                      py: 0,
                      height: periodRowControlHeight - 2,
                      boxSizing: 'border-box',
                      display: 'flex',
                      alignItems: 'center',
                      fontSize: '0.8125rem',
                      fontWeight: 500,
                      color: retentionTextPrimary,
                    },
                  }}
                >
                  <MenuItem value="none">None</MenuItem>
                  <MenuItem
                    value="previous_period"
                    disabled={!canUsePreviousPeriodCompare}
                  >
                    Previous period
                  </MenuItem>
                  <MenuItem
                    value="previous_year"
                    disabled={!canUsePreviousYearCompare}
                  >
                    Previous year
                  </MenuItem>
                  <MenuItem value="custom">Custom range</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {showCustomCompareDates && (
              <>
                <TextField
                  type="date"
                  label="From"
                  size="small"
                  value={
                    tempCustomCompareDates?.from ?? compareDateRange?.from ?? ''
                  }
                  onChange={(e) => {
                    const v = e.target.value;
                    const t =
                      tempCustomCompareDates?.to ?? compareDateRange?.to;
                    setTempCustomCompareDates(
                      t && v > t
                        ? { from: v, to: v }
                        : {
                            from: v,
                            to:
                              tempCustomCompareDates?.to ??
                              compareDateRange?.to ??
                              '',
                          },
                    );
                  }}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    min: lookbackMinDate || undefined,
                    max:
                      (tempCustomCompareDates?.to ?? compareDateRange?.to) ||
                      undefined,
                  }}
                  sx={{ flexShrink: 0, width: 140, ...periodRowControlSx }}
                />
                <TextField
                  type="date"
                  label="To"
                  size="small"
                  value={
                    tempCustomCompareDates?.to ?? compareDateRange?.to ?? ''
                  }
                  onChange={(e) => {
                    const v = e.target.value;
                    const f =
                      tempCustomCompareDates?.from ?? compareDateRange?.from;
                    setTempCustomCompareDates(
                      f && v < f
                        ? { from: f, to: f }
                        : {
                            from:
                              tempCustomCompareDates?.from ??
                              compareDateRange?.from ??
                              '',
                            to: v,
                          },
                    );
                  }}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    min:
                      (tempCustomCompareDates?.from ??
                        compareDateRange?.from) ||
                      undefined,
                    max: new Date().toISOString().split('T')[0],
                  }}
                  sx={{ flexShrink: 0, width: 140, ...periodRowControlSx }}
                />
                <Button
                  variant="contained"
                  size="small"
                  className={KIPIFY_FILTER_APPLY_BUTTON_CLASS}
                  onClick={() => {
                    if (
                      tempCustomCompareDates?.from &&
                      tempCustomCompareDates?.to &&
                      tempCustomCompareDates.from <= tempCustomCompareDates.to
                    ) {
                      if (!isRangeWithinLookback(tempCustomCompareDates)) {
                        snackbar(
                          getLookbackCustomRangeBlockedMessage(
                            lookbackDays,
                            'compare',
                          ),
                          'warning',
                        );
                        return;
                      }
                      setCompareDateRange(tempCustomCompareDates);
                      setShowCustomCompareDates(false);
                      setTempCustomCompareDates(null);
                    }
                  }}
                  disabled={
                    !tempCustomCompareDates?.from ||
                    !tempCustomCompareDates?.to ||
                    tempCustomCompareDates.from > tempCustomCompareDates.to
                  }
                  sx={{
                    flexShrink: 0,
                    height: periodRowControlHeight,
                    minHeight: periodRowControlHeight,
                    borderRadius: periodRowControlRadius,
                    bgcolor: palette.primary.main,
                    color: palette.primary.contrastText,
                    '&:hover': {
                      bgcolor: palette.primary.light,
                      opacity: 0.9,
                    },
                    minWidth: 60,
                  }}
                >
                  Apply
                </Button>
              </>
            )}

            <Box
              sx={{
                flex: '1 1 180px',
                minWidth: { xs: '100%', sm: 160, md: 200 },
                maxWidth: { xs: '100%', md: 400, lg: 480 },
                [theme.breakpoints.up('lg')]: { minWidth: 0 },
              }}
            >
              <Typography component="label" sx={labelSx}>
                Product
              </Typography>
              <Autocomplete
                size="small"
                fullWidth
                options={productSelectOptions}
                value={selectedProductOption}
                onChange={(_, next) => setProductId(next?.id ?? '')}
                getOptionLabel={(option) => option.title}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                autoHighlight
                componentsProps={{
                  paper: {
                    sx: productAutocompletePaperSx,
                  },
                }}
                renderInput={(params) => (
                  <TextField {...params} placeholder="Cerca prodotto…" />
                )}
                sx={productAutocompleteSx}
              />
            </Box>

            <Box
              sx={{
                display: 'flex',
                gap: 1,
                alignItems: 'flex-end',
                flexShrink: 0,
                flexWrap: 'nowrap',
                ml: { xs: 0, md: 'auto' },
              }}
            >
              <Button
                size="small"
                variant={showFilters ? 'contained' : 'outlined'}
                startIcon={<FilterListIcon />}
                endIcon={
                  <ExpandMoreIcon
                    sx={{
                      fontSize: 16,
                      transform: showFilters ? 'rotate(180deg)' : 'none',
                      transition: 'transform 0.2s ease',
                    }}
                  />
                }
                onClick={() => setShowFilters((current) => !current)}
                sx={{
                  minHeight: 40,
                  textTransform: 'none',
                  fontWeight: 600,
                  p: '10px',
                  borderRadius: 2,
                  whiteSpace: 'nowrap',
                  fontSize: '0.8125rem',
                  borderColor:
                    activeFilterCount > 0 ? palette.primary.main : '#e0e0e0',
                  color: showFilters ? '#fff' : retentionTextPrimary,
                  bgcolor: showFilters ? '#10b981' : 'white',
                  '&:hover': {
                    bgcolor: showFilters ? '#0f9f73' : 'rgba(15,23,42,0.04)',
                    borderColor: showFilters ? '#0f9f73' : palette.primary.main,
                    color: showFilters ? '#fff' : retentionTextPrimary,
                  },
                }}
              >
                Filters
                {activeFilterCount > 0 && (
                  <Chip
                    label={activeFilterCount}
                    size="small"
                    sx={{
                      ml: 0.75,
                      height: 19,
                      fontSize: 11,
                      fontWeight: 700,
                      bgcolor: showFilters
                        ? 'rgba(255,255,255,0.24)'
                        : palette.primary.main,
                      color: '#fff',
                    }}
                  />
                )}
              </Button>
              <Button
                variant="text"
                onClick={clearFilters}
                sx={{
                  minHeight: 40,
                  px: 0.75,
                  textTransform: 'none',
                  fontWeight: 600,
                  color: retentionTextSecondary,
                  '&:hover': {
                    bgcolor: 'rgba(15,23,42,0.06)',
                    color: retentionTextPrimary,
                  },
                }}
              >
                Reset
              </Button>
            </Box>
          </Box>

          {activeFilterCount > 0 && showFilters && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                mt: 1,
                mb: 1.25,
                flexWrap: 'wrap',
              }}
            >
              {countryFilter.map((item) => (
                <Chip
                  key={`ct-${item}`}
                  label={item}
                  size="small"
                  onDelete={() =>
                    setCountryFilter((current) =>
                      current.filter((value) => value !== item),
                    )
                  }
                  sx={{ borderRadius: 1, fontSize: 12, height: 26 }}
                />
              ))}
              {customerTypeFilter && (
                <Chip
                  label={
                    customerTypeFilter === 'First time' ? 'New' : 'Recurring'
                  }
                  size="small"
                  onDelete={() => setCustomerTypeFilter('')}
                  sx={{ borderRadius: 1, fontSize: 12, height: 26 }}
                />
              )}
              {tagFilter.map((item) => (
                <Chip
                  key={`tg-${item}`}
                  label={item}
                  size="small"
                  onDelete={() =>
                    setTagFilter((current) =>
                      current.filter((value) => value !== item),
                    )
                  }
                  sx={{ borderRadius: 1, fontSize: 12, height: 26 }}
                />
              ))}
              {collectionFilter.map((item) => (
                <Chip
                  key={`cl-${item}`}
                  label={item}
                  size="small"
                  onDelete={() =>
                    setCollectionFilter((current) =>
                      current.filter((value) => value !== item),
                    )
                  }
                  sx={{ borderRadius: 1, fontSize: 12, height: 26 }}
                />
              ))}
              {vendorFilter.map((item) => (
                <Chip
                  key={`vd-${item}`}
                  label={item}
                  size="small"
                  onDelete={() =>
                    setVendorFilter((current) =>
                      current.filter((value) => value !== item),
                    )
                  }
                  sx={{ borderRadius: 1, fontSize: 12, height: 26 }}
                />
              ))}
              {productTypeFilter.map((item) => (
                <Chip
                  key={`pt-${item}`}
                  label={item}
                  size="small"
                  onDelete={() =>
                    setProductTypeFilter((current) =>
                      current.filter((value) => value !== item),
                    )
                  }
                  sx={{ borderRadius: 1, fontSize: 12, height: 26 }}
                />
              ))}
            </Box>
          )}

          <Collapse in={showFilters} timeout={200}>
            <Box
              sx={{
                mt: 1,
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' },
                gap: 1.5,
                mb: 0.75,
                p: 2,
                borderRadius: 2,
                backgroundColor: '#fafafa',
                border: '1px solid #f0f0f0',
              }}
            >
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    mb: 0.5,
                    display: 'block',
                    fontWeight: 600,
                    color: retentionTextSecondary,
                  }}
                >
                  Country
                </Typography>
                <Autocomplete
                  multiple
                  size="small"
                  disableCloseOnSelect
                  options={availableCountries}
                  value={countryFilter}
                  onChange={(_, value) => setCountryFilter(value)}
                  renderOption={(props, option, { selected }) => (
                    <li {...props}>
                      <Checkbox
                        checked={selected}
                        sx={{ mr: 0.5, p: 0 }}
                        size="small"
                      />
                      <Typography variant="body2">{option}</Typography>
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select countries..."
                      sx={filterPanelInputSx}
                    />
                  )}
                  renderTags={() => null}
                />
              </Box>

              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    mb: 0.5,
                    display: 'block',
                    fontWeight: 600,
                    color: retentionTextSecondary,
                  }}
                >
                  Customer type
                </Typography>
                <Select
                  size="small"
                  fullWidth
                  displayEmpty
                  value={customerTypeFilter}
                  onChange={(event) => {
                    const value = event.target
                      .value as InventoryLikeCustomerType;
                    setCustomerTypeFilter(value);
                  }}
                  sx={filterPanelSelectSx}
                  renderValue={(value) =>
                    value ? (
                      value === 'First time' ? (
                        'New (first time)'
                      ) : (
                        'Recurring'
                      )
                    ) : (
                      <Typography sx={{ color: retentionTextSecondary }}>
                        All customers
                      </Typography>
                    )
                  }
                >
                  <MenuItem value="">All customers</MenuItem>
                  <MenuItem value="First time">New (first time)</MenuItem>
                  <MenuItem value="recurring">Recurring</MenuItem>
                </Select>
              </Box>

              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    mb: 0.5,
                    display: 'block',
                    fontWeight: 600,
                    color: retentionTextSecondary,
                  }}
                >
                  Tag
                </Typography>
                <Autocomplete
                  multiple
                  size="small"
                  disableCloseOnSelect
                  options={availableOrderTags}
                  value={tagFilter}
                  onChange={(_, value) => setTagFilter(value)}
                  renderOption={(props, option, { selected }) => (
                    <li {...props}>
                      <Checkbox
                        checked={selected}
                        sx={{ mr: 0.5, p: 0 }}
                        size="small"
                      />
                      <Typography variant="body2">{option}</Typography>
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select tags..."
                      sx={filterPanelInputSx}
                    />
                  )}
                  renderTags={() => null}
                />
              </Box>

              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    mb: 0.5,
                    display: 'block',
                    fontWeight: 600,
                    color: retentionTextSecondary,
                  }}
                >
                  Collection
                </Typography>
                <Autocomplete
                  multiple
                  size="small"
                  disableCloseOnSelect
                  options={availableCollectionTitles}
                  value={collectionFilter}
                  onChange={(_, value) => setCollectionFilter(value)}
                  renderOption={(props, option, { selected }) => (
                    <li {...props}>
                      <Checkbox
                        checked={selected}
                        sx={{ mr: 0.5, p: 0 }}
                        size="small"
                      />
                      <Typography variant="body2">{option}</Typography>
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select collections..."
                      sx={filterPanelInputSx}
                    />
                  )}
                  renderTags={() => null}
                />
              </Box>

              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    mb: 0.5,
                    display: 'block',
                    fontWeight: 600,
                    color: retentionTextSecondary,
                  }}
                >
                  Vendor
                </Typography>
                <Autocomplete
                  multiple
                  size="small"
                  disableCloseOnSelect
                  options={availableVendors}
                  value={vendorFilter}
                  onChange={(_, value) => setVendorFilter(value)}
                  renderOption={(props, option, { selected }) => (
                    <li {...props}>
                      <Checkbox
                        checked={selected}
                        sx={{ mr: 0.5, p: 0 }}
                        size="small"
                      />
                      <Typography variant="body2">{option}</Typography>
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select vendors..."
                      sx={filterPanelInputSx}
                    />
                  )}
                  renderTags={() => null}
                />
              </Box>

              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    mb: 0.5,
                    display: 'block',
                    fontWeight: 600,
                    color: retentionTextSecondary,
                  }}
                >
                  Product Type
                </Typography>
                <Autocomplete
                  multiple
                  size="small"
                  disableCloseOnSelect
                  options={availableProductTypes}
                  value={productTypeFilter}
                  onChange={(_, value) => setProductTypeFilter(value)}
                  renderOption={(props, option, { selected }) => (
                    <li {...props}>
                      <Checkbox
                        checked={selected}
                        sx={{ mr: 0.5, p: 0 }}
                        size="small"
                      />
                      <Typography variant="body2">{option}</Typography>
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select types..."
                      sx={filterPanelInputSx}
                    />
                  )}
                  renderTags={() => null}
                />
              </Box>
            </Box>
          </Collapse>

          {lookbackDays !== null && lookbackDays !== undefined && (
            <Box
              sx={{
                mt: 1.5,
                px: 1.25,
                py: 1,
                borderRadius: 1.5,
                bgcolor: 'rgba(239, 171, 0, 0.12)',
                border: '1px solid rgba(239, 171, 0, 0.35)',
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontSize: '0.8125rem',
                  fontWeight: 600,
                  color: '#7a5200',
                  lineHeight: 1.45,
                }}
              >
                {getLookbackPlanLimitCaption(lookbackDays)}
              </Typography>
            </Box>
          )}
          <Typography
            variant="body2"
            component="p"
            sx={{
              mt: 1.25,
              pt: 1.25,
              borderTop: '1px solid rgba(15, 23, 42, 0.06)',
              fontSize: '0.8125rem',
              color: retentionTextSecondary,
              lineHeight: 1.5,
              maxWidth: 920,
              m: 0,
            }}
          >
            Metrics are anchored to first-purchase cohorts in the selected
            period, so reorder timing stays tied to the chosen window. Charts
            and tables use the primary period; with Compare on, the tiles show
            the comparison cohort under the main value.
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2.5 }}>
            {error.message}
          </Alert>
        )}

        {compareError && (
          <Alert severity="warning" sx={{ mb: 2, borderRadius: 2.5 }}>
            Compare period could not be loaded: {compareError.message}
          </Alert>
        )}

        {analytics?.warnings.map((warning) => (
          <Alert
            key={warning.code}
            severity="warning"
            sx={{
              mb: 2,
              borderRadius: 2.5,
              '& .MuiAlert-message': {
                color: retentionTextPrimary,
                fontWeight: 500,
              },
            }}
          >
            {warning.message}
          </Alert>
        ))}

        {listLoading ? (
          <RetentionLoadingState />
        ) : analytics?.summary.customersAnalyzed === 0 ? (
          <Card sx={{ borderRadius: 3 }}>
            <CardContent sx={{ textAlign: 'center', py: 5 }}>
              <Typography
                variant="h6"
                sx={{ color: palette.text.primary, fontWeight: 700 }}
              >
                Not enough valid customer history yet
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: retentionTextSecondary,
                  mt: 1.25,
                  mb: 2,
                  lineHeight: 1.5,
                }}
              >
                Try widening the date range or check that Shopify orders and
                customers are synced.
              </Typography>
              <Button
                variant="contained"
                onClick={() => {
                  setDateRangePreset('90');
                  setShowCustomDateRange(false);
                  setTempCustomDates(null);
                  const next = getLastNDaysDateRange(90);
                  setDateRange(next);
                  if (compareMode === 'previous_period')
                    setCompareDateRange(getPreviousPeriodDateRange(next));
                  else if (compareMode === 'previous_year')
                    setCompareDateRange(getPreviousYearDateRange(next));
                }}
                sx={{ textTransform: 'none', fontWeight: 700 }}
              >
                Try last 90 days
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Stack spacing={2}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, minmax(0, 1fr))',
                  lg: 'repeat(4, minmax(0, 1fr))',
                },
                gap: 2,
              }}
            >
              <MetricCard
                label="Avg days to 2nd purchase"
                tooltip="Average number of days between the first and second valid order."
                value={formatWholeDays(
                  analytics?.summary.avgDaysToSecondPurchase,
                )}
                caption={`Median ${formatWholeDays(analytics?.summary.medianDaysToSecondPurchase).toLowerCase()}`}
                compareDelta={metricCompareDeltas.avgSecond}
                accent="#22c55e"
                kpiIcon="calendar"
              />
              <MetricCard
                label="Avg days to reorder same product"
                tooltip="Average number of days until any product from the first order is bought again."
                value={formatWholeDays(
                  analytics?.summary.avgDaysToSameProductReorder,
                )}
                caption="Measured on customers who reordered at least one product from their first order."
                compareDelta={metricCompareDeltas.avgSame}
                accent="#22c55e"
                kpiIcon="clock"
              />
              <MetricCard
                label="Top first purchase driving repeats"
                tooltip="First-order product shared by the most customers who went on to place a second order (ranked by volume)."
                value={
                  analytics?.summary.topFirstPurchaseProduct?.productTitle ??
                  'Not enough data'
                }
                caption={(() => {
                  const summaryTop = analytics?.summary.topFirstPurchaseProduct;
                  const row =
                    analytics?.firstPurchaseProductsGeneratingRepeats?.[0];
                  if (!summaryTop || !row) {
                    return 'Need more repeat orders to identify a leading first product.';
                  }
                  const sameProduct =
                    (summaryTop.productId != null &&
                      row.productId === summaryTop.productId) ||
                    row.productTitle === summaryTop.productTitle;
                  if (!sameProduct) {
                    return `${formatPercent(summaryTop.repeatRate)} repeat rate`;
                  }
                  return `${row.repeatCustomers} returning customers · ${formatPercent(
                    summaryTop.repeatRate,
                  )} repeat rate`;
                })()}
                compareDelta={metricCompareDeltas.topProduct}
                accent="#14b8a6"
                kpiIcon="bag"
                valueMode="entity"
              />
              <MetricCard
                label="Win-back timing benchmark"
                tooltip="Among customers who do place a second order, 75% do it within this many days of their first order—so it is a practical cue for when to send your first reminder or offer. Anyone who never orders twice is not part of this number; use repeat rate and the days chart for the full picture."
                value={formatWholeDays(analytics?.summary.inactivityWindowDays)}
                caption={`${
                  analytics?.summary.inactiveCustomersAfter60Days ?? 0
                } customers with no second order within 60 days · ${
                  analytics?.summary.inactiveCustomersAfter90Days ?? 0
                } within 90 days`}
                compareDelta={metricCompareDeltas.inactivity}
                accent="#f59e0b"
                kpiIcon="person"
              />
              <MetricCard
                label="Cumulative LTV"
                tooltip="Total cumulative revenue generated by the analyzed cohort, divided by customers in that cohort."
                value={formatCurrency(analytics?.summary.cumulativeLtv)}
                caption={`${analytics?.summary.customersAnalyzed ?? 0} customers in this cohort`}
                compareDelta={metricCompareDeltas.ltv}
                accent="#10b981"
                kpiIcon="money"
              />
              <MetricCard
                label="Gross margin LTV"
                tooltip="Revenue minus product costs, discounts, and refunds, divided by customers. This is before media spend."
                value={formatCurrency(
                  analytics?.summary.cumulativeGrossMarginLtv,
                )}
                caption={`After product costs, before ad spend · ${formatPercent(
                  analytics?.summary.grossMarginCostCoverage,
                )} COGS coverage`}
                compareDelta={metricCompareDeltas.grossMarginLtv}
                accent="#6366f1"
                kpiIcon="money"
              />
              <MetricCard
                label="Best LTV starter product"
                tooltip="The first-purchase product whose starter cohort produces the strongest LTV once audience size is taken into account."
                value={
                  analytics?.summary.topLtvStarterProduct?.productTitle ??
                  'Not enough data'
                }
                caption={
                  analytics?.summary.topLtvStarterProduct
                    ? `${formatCompactCurrency(
                        analytics?.summary.topLtvStarterProduct?.ltv,
                      )} avg LTV · ${
                        analytics?.summary.topLtvStarterProduct
                          ?.firstPurchaseCustomers ?? 0
                      } first buyers`
                    : 'Need more first-order history to rank starter-product LTV.'
                }
                accent="#14b8a6"
                kpiIcon="bag"
                valueMode="entity"
              />
              <MetricCard
                label="LTV : CAC ratio"
                tooltip="Cumulative LTV divided by blended CAC from Meta and Google Ads in the selected period."
                value={formatRatio(analytics?.summary.ltvToCacRatio)}
                caption={
                  analytics?.summary.blendedAdSpend !== null
                    ? `CAC ${formatCompactCurrency(
                        analytics?.summary.averageBlendedCac,
                      )} from Meta ${formatCompactCurrency(
                        analytics?.summary.metaAdSpend,
                      )} + Google ${formatCompactCurrency(
                        analytics?.summary.googleAdSpend,
                      )}`
                    : 'Connect Meta or Google Ads to compute blended CAC.'
                }
                compareDelta={metricCompareDeltas.ltvCacRatio}
                accent="#f59e0b"
                kpiIcon="trend"
              />
            </Box>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  md: 'repeat(2, minmax(0, 1fr))',
                },
                gap: 2,
              }}
            >
              <Box sx={{ gridColumn: '1 / -1' }}>
                <SectionCard
                  title="Days to second purchase"
                  tooltip="How long people wait before order #2. Each bar is a share of your analyzed customers; use it with the win-back benchmark card above."
                >
                  {hasSecondPurchaseData ? (
                    <DaysToSecondPurchaseChart
                      data={analytics?.daysToSecondPurchaseDistribution ?? []}
                    />
                  ) : (
                    <Typography sx={{ color: retentionTextSecondary }}>
                      No second-purchase events yet for the selected cohort.
                    </Typography>
                  )}
                </SectionCard>
              </Box>
              <Box>
                <SectionCard
                  title="First-purchase products driving repeat customers"
                  tooltip="Products people bought on their first order with you. We show how many of those customers later placed another order. The ranking favours products where both the comeback count and the audience size are meaningful, so a handful of orders does not dominate the list."
                  action={
                    (analytics?.firstPurchaseProductsGeneratingRepeats
                      ?.length ?? 0) > 5 ? (
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => setRepeatDriversModalOpen(true)}
                        sx={{
                          fontWeight: 700,
                          textTransform: 'none',
                          borderRadius: 2,
                          flexShrink: 0,
                          fontSize: '0.75rem',
                          py: 0.35,
                          px: 1.1,
                        }}
                      >
                        Top 20
                      </Button>
                    ) : undefined
                  }
                >
                  {(analytics?.firstPurchaseProductsGeneratingRepeats?.length ??
                    0) > 0 ? (
                    <FirstPurchaseRepeatChart
                      data={
                        analytics?.firstPurchaseProductsGeneratingRepeats ?? []
                      }
                    />
                  ) : (
                    <Typography sx={{ color: retentionTextSecondary }}>
                      Not enough first-order product history to rank repeat
                      drivers yet.
                    </Typography>
                  )}
                </SectionCard>
              </Box>
              <Box>
                <SectionCard
                  title="First-purchase products with most follow-up orders"
                  tooltip="Same first-order lens, but ranked by how many extra orders those buyers placed in total (2nd, 3rd, and so on). Strong for spotting habits and heavy buyers, not just one comeback."
                  action={
                    (analytics?.firstPurchaseProductsByFollowUpOrderVolume
                      ?.length ?? 0) > 5 ? (
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => setFollowUpVolumeModalOpen(true)}
                        sx={{
                          fontWeight: 700,
                          textTransform: 'none',
                          borderRadius: 2,
                          flexShrink: 0,
                          fontSize: '0.75rem',
                          py: 0.35,
                          px: 1.1,
                        }}
                      >
                        Top 20
                      </Button>
                    ) : undefined
                  }
                >
                  {(analytics?.firstPurchaseProductsByFollowUpOrderVolume
                    ?.length ?? 0) > 0 ? (
                    <FirstPurchaseFollowUpOrdersChart
                      data={
                        analytics?.firstPurchaseProductsByFollowUpOrderVolume ??
                        []
                      }
                    />
                  ) : (
                    <Typography sx={{ color: retentionTextSecondary }}>
                      Not enough order history to rank follow-up volume yet.
                    </Typography>
                  )}
                </SectionCard>
              </Box>
            </Box>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  md: 'repeat(2, minmax(0, 1fr))',
                },
                gap: 2,
              }}
            >
              <Box>
                <SectionCard
                  title="First-purchase products generating the highest LTV"
                  tooltip="Starter products ranked by the value their cohorts generate over time, not just by second-order rate."
                  action={
                    (analytics?.firstPurchaseProductsByLtv?.length ?? 0) > 5 ? (
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => setLtvDriversModalOpen(true)}
                        sx={{
                          fontWeight: 700,
                          textTransform: 'none',
                          borderRadius: 2,
                          flexShrink: 0,
                          fontSize: '0.75rem',
                          py: 0.35,
                          px: 1.1,
                        }}
                      >
                        Top 20
                      </Button>
                    ) : undefined
                  }
                >
                  {hasLtvProductData ? (
                    <FirstPurchaseLtvChart
                      data={analytics?.firstPurchaseProductsByLtv ?? []}
                    />
                  ) : (
                    <Typography sx={{ color: retentionTextSecondary }}>
                      Not enough order value history to rank starter-product LTV
                      yet.
                    </Typography>
                  )}
                </SectionCard>
              </Box>

              <Box>
                <SectionCard
                  title="One-time vs repeat customer economics"
                  tooltip="A simple segment view showing how much more value repeat buyers generate once they place follow-up orders."
                >
                  {hasCustomerEconomicsData ? (
                    <CustomerEconomicsTable
                      rows={analytics?.customerEconomics ?? []}
                    />
                  ) : (
                    <Typography sx={{ color: retentionTextSecondary }}>
                      Not enough cohort data to split one-time and repeat buyer
                      economics yet.
                    </Typography>
                  )}
                </SectionCard>
              </Box>
            </Box>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  md: 'repeat(2, minmax(0, 1fr))',
                },
                gap: 2,
              }}
            >
              <Box sx={{ gridColumn: '1 / -1' }}>
                <SectionCard
                  title="Cohort LTV curve"
                  tooltip="How the cohort matures in value over time across revenue, gross margin, orders per customer, and LTV:CAC (when Meta/Google spend is available)."
                >
                  <CohortLtvCurveChart data={analytics?.cohortLtvCurve ?? []} />
                </SectionCard>
              </Box>

              <Box sx={{ gridColumn: '1 / -1' }}>
                <SectionCard
                  title="Second purchase timing"
                  tooltip="Cumulative % who placed a second order within each day window. Only counts shoppers with enough days after their first order before the end of your range—pair with repeat rate and the win-back hint on-chart."
                >
                  <PurchaseDecayChart
                    data={analytics?.purchaseDecayCurve ?? []}
                  />
                </SectionCard>
              </Box>

              <Box>
                <SectionCard
                  title="Most common second purchases"
                  tooltip="Most frequent product transitions from the first order to the second order."
                  action={
                    (analytics?.mostCommonSecondPurchases?.length ?? 0) > 6 ? (
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => setSecondPairsModalOpen(true)}
                        sx={{
                          fontWeight: 700,
                          textTransform: 'none',
                          borderRadius: 2,
                          flexShrink: 0,
                          fontSize: '0.75rem',
                          py: 0.35,
                          px: 1.1,
                        }}
                      >
                        Top 20
                      </Button>
                    ) : undefined
                  }
                >
                  {hasTableData ? (
                    <SecondPurchasesTable
                      rows={analytics?.mostCommonSecondPurchases ?? []}
                    />
                  ) : (
                    <Typography sx={{ color: retentionTextSecondary }}>
                      Not enough second-order transitions to build the table
                      yet.
                    </Typography>
                  )}
                </SectionCard>
              </Box>

              <Box>
                <SectionCard
                  title="What to run next"
                  tooltip="Prioritized plays from this cohort—reorder windows, hero SKUs, and win-back timing. Same filters as the charts: use it to brief your team, your agency, or your next sprint."
                  action={
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#6366f1',
                        fontWeight: 700,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.5,
                      }}
                    >
                      <InsightsRoundedIcon sx={{ fontSize: 14 }} />
                      From this cohort
                    </Typography>
                  }
                >
                  {hasInsights ? (
                    <InsightsPanel insights={analytics?.insights ?? []} />
                  ) : (
                    <Typography sx={{ color: retentionTextSecondary }}>
                      Widen the date range or loosen filters—once repeat
                      behavior shows up, we stack founder-ready plays here.
                    </Typography>
                  )}
                </SectionCard>
              </Box>
            </Box>
          </Stack>
        )}

        <Dialog
          open={repeatDriversModalOpen}
          onClose={() => setRepeatDriversModalOpen(false)}
          maxWidth="sm"
          fullWidth
          scroll="paper"
        >
          <DialogTitle
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 1,
              pr: 0.5,
              pt: 2,
              pb: 1,
            }}
          >
            <Box sx={{ minWidth: 0, pr: 1 }}>
              <Typography
                component="span"
                variant="h6"
                sx={{ fontWeight: 800, display: 'block', lineHeight: 1.25 }}
              >
                Repeat drivers
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: retentionTextSecondary,
                  mt: 0.5,
                  display: 'block',
                  lineHeight: 1.4,
                  fontWeight: 500,
                }}
              >
                Up to 20 first-purchase products for your current filters—same
                ranking as the card.
              </Typography>
            </Box>
            <IconButton
              aria-label="Close"
              size="small"
              onClick={() => setRepeatDriversModalOpen(false)}
              sx={{ mt: -0.25 }}
            >
              <CloseRoundedIcon fontSize="small" />
            </IconButton>
          </DialogTitle>
          <DialogContent
            dividers
            sx={{
              pt: 2,
              maxHeight: 'min(70vh, 720px)',
              overflowY: 'auto',
            }}
          >
            <FirstPurchaseRepeatProductList
              data={analytics?.firstPurchaseProductsGeneratingRepeats ?? []}
              maxRows={20}
              layout="modal"
            />
          </DialogContent>
        </Dialog>

        <Dialog
          open={followUpVolumeModalOpen}
          onClose={() => setFollowUpVolumeModalOpen(false)}
          maxWidth="sm"
          fullWidth
          scroll="paper"
        >
          <DialogTitle
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 1,
              pr: 0.5,
              pt: 2,
              pb: 1,
            }}
          >
            <Box sx={{ minWidth: 0, pr: 1 }}>
              <Typography
                component="span"
                variant="h6"
                sx={{ fontWeight: 800, display: 'block', lineHeight: 1.25 }}
              >
                Follow-up order volume
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: retentionTextSecondary,
                  mt: 0.5,
                  display: 'block',
                  lineHeight: 1.4,
                  fontWeight: 500,
                }}
              >
                Up to 20 first-purchase products ranked like the card—bars scale
                to the top row in this list.
              </Typography>
            </Box>
            <IconButton
              aria-label="Close"
              size="small"
              onClick={() => setFollowUpVolumeModalOpen(false)}
              sx={{ mt: -0.25 }}
            >
              <CloseRoundedIcon fontSize="small" />
            </IconButton>
          </DialogTitle>
          <DialogContent
            dividers
            sx={{
              pt: 2,
              maxHeight: 'min(70vh, 720px)',
              overflowY: 'auto',
            }}
          >
            <FirstPurchaseFollowUpOrdersList
              data={analytics?.firstPurchaseProductsByFollowUpOrderVolume ?? []}
              maxRows={20}
              layout="modal"
            />
          </DialogContent>
        </Dialog>

        <Dialog
          open={ltvDriversModalOpen}
          onClose={() => setLtvDriversModalOpen(false)}
          maxWidth="sm"
          fullWidth
          scroll="paper"
        >
          <DialogTitle
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 1,
              pr: 0.5,
              pt: 2,
              pb: 1,
            }}
          >
            <Box sx={{ minWidth: 0, pr: 1 }}>
              <Typography
                component="span"
                variant="h6"
                sx={{ fontWeight: 800, display: 'block', lineHeight: 1.25 }}
              >
                Starter product LTV
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: retentionTextSecondary,
                  mt: 0.5,
                  display: 'block',
                  lineHeight: 1.4,
                  fontWeight: 500,
                }}
              >
                Up to 20 first-purchase products for your current filters,
                ranked like the card.
              </Typography>
            </Box>
            <IconButton
              aria-label="Close"
              size="small"
              onClick={() => setLtvDriversModalOpen(false)}
              sx={{ mt: -0.25 }}
            >
              <CloseRoundedIcon fontSize="small" />
            </IconButton>
          </DialogTitle>
          <DialogContent
            dividers
            sx={{
              pt: 2,
              maxHeight: 'min(70vh, 720px)',
              overflowY: 'auto',
            }}
          >
            <FirstPurchaseLtvList
              data={analytics?.firstPurchaseProductsByLtv ?? []}
              maxRows={20}
              layout="modal"
            />
          </DialogContent>
        </Dialog>

        <Dialog
          open={secondPairsModalOpen}
          onClose={() => setSecondPairsModalOpen(false)}
          maxWidth="md"
          fullWidth
          scroll="paper"
        >
          <DialogTitle
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 1,
              pr: 0.5,
              pt: 2,
              pb: 1,
            }}
          >
            <Box sx={{ minWidth: 0, pr: 1 }}>
              <Typography
                component="span"
                variant="h6"
                sx={{ fontWeight: 800, display: 'block', lineHeight: 1.25 }}
              >
                First → second product pairs
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: retentionTextSecondary,
                  mt: 0.5,
                  display: 'block',
                  lineHeight: 1.4,
                  fontWeight: 500,
                }}
              >
                Up to 20 rows for your current filters (same sort as the table).
              </Typography>
            </Box>
            <IconButton
              aria-label="Close"
              size="small"
              onClick={() => setSecondPairsModalOpen(false)}
              sx={{ mt: -0.25 }}
            >
              <CloseRoundedIcon fontSize="small" />
            </IconButton>
          </DialogTitle>
          <DialogContent
            dividers
            sx={{
              pt: 2,
              maxHeight: 'min(75vh, 800px)',
              overflowY: 'auto',
            }}
          >
            <SecondPurchasesTable
              rows={analytics?.mostCommonSecondPurchases ?? []}
              maxRows={20}
            />
          </DialogContent>
        </Dialog>

        <Dialog
          open={aiReportOpen}
          onClose={() => setAiReportOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle sx={{ fontWeight: 800 }}>
            Your retention playbook
          </DialogTitle>
          <DialogContent>
            <Stack spacing={2}>
              <Alert
                severity="info"
                sx={{
                  borderRadius: 2.5,
                  '& .MuiAlert-message': {
                    color: retentionTextPrimary,
                  },
                }}
              >
                Snapshot of the metrics you are already viewing—packaged as
                plays you can ship. It mirrors your filters and date range; use
                it to align growth, lifecycle, and merch on one narrative.
              </Alert>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, minmax(0, 1fr))',
                  },
                  gap: 1.5,
                }}
              >
                <Box>
                  <Card variant="outlined" sx={{ borderRadius: 2.5 }}>
                    <CardContent>
                      <Typography
                        variant="body2"
                        sx={{ color: retentionTextSecondary }}
                      >
                        Customers analyzed
                      </Typography>
                      <Typography
                        variant="h5"
                        sx={{ fontWeight: 800, mt: 0.75 }}
                      >
                        {analytics?.summary.customersAnalyzed ?? 0}
                      </Typography>
                    </CardContent>
                  </Card>
                </Box>
                <Box>
                  <Card variant="outlined" sx={{ borderRadius: 2.5 }}>
                    <CardContent>
                      <Typography
                        variant="body2"
                        sx={{ color: retentionTextSecondary }}
                      >
                        Repeat purchase rate
                      </Typography>
                      <Typography
                        variant="h5"
                        sx={{ fontWeight: 800, mt: 0.75 }}
                      >
                        {formatPercent(analytics?.summary.repeatPurchaseRate)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Box>
              </Box>

              {hasInsights ? (
                <InsightsPanel insights={analytics?.insights ?? []} />
              ) : (
                <Typography sx={{ color: retentionTextSecondary }}>
                  Not enough signal with this slice yet—extend the period or
                  relax filters to fill the playbook.
                </Typography>
              )}
            </Stack>
          </DialogContent>
        </Dialog>
      </ContentContainer>
    </PageContainer>
  );
};

export default RetentionPage;
